import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { always, flatten, pick, values } from 'ramda';
import { useRouter } from 'next/router';
import useLogout from '@bigcommerce/storefront-data-hooks/use-logout';
import NextLink from 'next/link';
import { useAsync } from 'react-use';

import { Header } from '@components/header';
import { Layout } from '@components/layout';
import { ArtworksGrid } from '@components/PLP/artworksGrid';
import { GridSwitcher } from '@components/PLP/gridSwitcher';
import { useChangeGridSize } from '@hooks/useArtworks';
import { HeaderFilter } from '@components/headerFilter';
import { useFilters } from '@hooks/useFilters';
import reportError from '@utils/reportError';
import { HEADER_TOP, SUBLEVEL } from '@components/header/header.constants';
import { MAIN_ANIMATION, ROUTE } from '@utils/constants';
import { renderWhenTrue } from '@utils/rendering';
import { useHeaderMobileMenu, useHeaderSublevel } from '@hooks/useUi/useUi';
import { useGroupings } from '@hooks/useHeader';
import { useCustomer } from '@hooks/useCustomer';
import { FavoriteDropdown } from '@components/favoriteDropdown';
import { FocusTrap } from '@components/common';
import { Artwork } from '@definitions/artworks.types';
import { Grouping as GroupingType } from '@definitions/header.types';
import { SortDropdown } from '@components/sortDropdown';
import { ls } from '@utils/helpers';
import { useArtworksRoute } from '@hooks/useArtworks/useArtworks';
import { USER_WAITLISTED_ITEMS } from '@components/waitlistButton/waitlistButton.constants';
import { eUserVariants, tUserVariant } from '@components/tradeRoute/tradeRoute.component';
import { FILTER_ORDER } from '@components/headerFilter/headerFilter.constants';

import {
  Container,
  MenuOption,
  MotionContainer,
  TradeModal,
  Content,
  Title,
  Controls,
  Button,
  DesktopGroupingsContainer,
  GroupingsList,
  Grouping,
  ControlsContainer,
  MobileGroupingsButton,
  GroupingIntro,
  GroupingTitle,
  GroupingDescription,
  AllArtworksGrouping,
  TradeProgram,
} from './artworksView.styles';

export enum DROPDOWN_TYPES {
  FILTER = 'filter',
  SORT = 'sort',
  FAVOURITE = 'favourite',
}

const TRADE_PROGRAM_LANDING =
  'https://www.platformart.com/features/platform-trade-program-art-advisors-interior-designers';

export const FILTER_BUTTON_TEST_ID = 'filter-button-test-id';

export interface ArtworksViewProps {
  artworks: Artwork[];
  isPageAvailable?: boolean;
  userVariant?: tUserVariant;
}

export const ArtworksView = ({ artworks, isPageAvailable, userVariant }: ArtworksViewProps) => {
  const { groupings, fetchGroupings } = useGroupings();
  const { setHeaderMobileMenuOpen } = useHeaderMobileMenu();
  const { openHeaderSublevel } = useHeaderSublevel();
  const intl = useIntl();
  const { isAuthed } = useCustomer();
  const router = useRouter();
  const logout = useLogout();
  const { isTradeMemberRoute } = useArtworksRoute();
  const [filtersCount, setFiltersCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState<DROPDOWN_TYPES | null>(null);
  const favoriteOpen = dropdownOpen === DROPDOWN_TYPES.FAVOURITE;
  const filterOpen = dropdownOpen === DROPDOWN_TYPES.FILTER;
  const { gridSize } = useChangeGridSize();
  const headerTitle = intl.formatMessage({
    id: 'artworksView.headerTitle',
    defaultMessage: 'Buy Art Online | Online Art Gallery',
  });
  const headerDescription = intl.formatMessage({
    id: 'artworksView.headerDescription',
    defaultMessage:
      'Browse all works in our online art gallery on this page. Find something you love curated by experts at PLATFORM to easily and quickly buy art online.',
  });
  const { filterOptions, fetchFilterOptions } = useFilters();
  const groupingSlug = router.query.grouping;

  const getGroupingIntro = () => {
    const currentItem = groupings?.filter((i) => i.slug === groupingSlug)[0];
    return {
      groupingDescription: currentItem?.description || '',
      groupingTitle: currentItem?.name,
    };
  };

  const { groupingDescription, groupingTitle } = getGroupingIntro();

  useAsync(async () => {
    await fetchGroupings();
  });

  useEffect(() => {
    callFetchFilterOptions().finally(() => ({}));
  }, []);

  const callFetchFilterOptions = async () => {
    try {
      await fetchFilterOptions();
    } catch (e) {
      reportError(e);
    }
  };

  const handleOnLoginClick = async () => {
    try {
      await logout();
      ls.remove(USER_WAITLISTED_ITEMS);
      openHeaderSublevel({ sublevel: SUBLEVEL.ACCOUNT, preventRedirectAfterLogin: true });
    } catch (e) {
      reportError(e);
    }
  };

  useAsync(async () => {
    if (groupings && groupings.length > 0 && router.pathname === ROUTE.ARTWORKS) {
      const defaultGrouping = groupings[0].slug;
      await router.push(`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${defaultGrouping}`);
    }
  }, [groupings]);

  useEffect(() => {
    setFiltersCount(flatten(values(pick([...FILTER_ORDER, 'inputPrice'], router.query))).length);
  }, [router.query]);

  const toggleDropdown = (type: DROPDOWN_TYPES) => () =>
    type === dropdownOpen ? setDropdownOpen(null) : setDropdownOpen(type);

  const renderFavoriteDropdown = renderWhenTrue(always(<FavoriteDropdown isOpen={favoriteOpen} />));
  const renderFocusTrap = renderWhenTrue(
    always(<FocusTrap onFocus={() => setDropdownOpen(DROPDOWN_TYPES.FAVOURITE)} />)
  );

  const renderFiltersCount = renderWhenTrue(always(`: ${filtersCount}`));

  const renderAllArtworksItem = renderWhenTrue(
    always(<AllArtworksGrouping isActive={router.pathname === ROUTE.ARTWORKS}>All Artworks</AllArtworksGrouping>)
  );

  const renderMobileAllArtworksItem = renderWhenTrue(
    always(<MobileGroupingsButton onClick={() => setHeaderMobileMenuOpen(true)}>All Artworks</MobileGroupingsButton>)
  );

  const renderMobileGroupingsButton = renderWhenTrue(
    always(<MobileGroupingsButton onClick={() => setHeaderMobileMenuOpen(true)}>{groupingTitle}</MobileGroupingsButton>)
  );

  const renderTradeProgramLink = (idx: number) => (
    <NextLink key={idx} href={ROUTE.TRADE} passHref>
      <TradeProgram isActive={router.pathname === ROUTE.TRADE}>
        <FormattedMessage id="artworksView.tradeProgram" defaultMessage="Trade Program" />
      </TradeProgram>
    </NextLink>
  );

  const renderGroupingLink = (grouping: GroupingType, idx: number) => (
    <NextLink
      key={idx}
      href={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/[grouping]`}
      as={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`}
      passHref
    >
      <Grouping isActive={router.asPath === `${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`}>
        {grouping.name}
      </Grouping>
    </NextLink>
  );

  const renderSecNavBarLink = (grouping: GroupingType, idx: number) => {
    return grouping.slug == 'trade' ? renderTradeProgramLink(idx) : renderGroupingLink(grouping, idx);
  };

  const renderGroupings = renderWhenTrue(
    always(
      <GroupingsList isHidden={!groupings || groupings.length === 0}>
        {groupings?.map((grouping, idx) => renderSecNavBarLink(grouping, idx))}
      </GroupingsList>
    )
  );

  const renderFilterButton = renderWhenTrue(
    always(
      <MenuOption onClick={toggleDropdown(DROPDOWN_TYPES.FILTER)} data-testid={FILTER_BUTTON_TEST_ID}>
        <FormattedMessage id="artworksView.filterMenu" defaultMessage="Filter" />
        {renderFiltersCount(!!filtersCount)}
      </MenuOption>
    )
  );

  const renderCustomHeader = () => (
    <Header
      headerTop={HEADER_TOP.REGULAR}
      childrenSize={51}
      isChildrenOpen={filterOpen}
      handleChildrenClose={() => setDropdownOpen(null)}
    >
      <MotionContainer
        initial={{ opacity: 0, y: -44 }}
        animate={{ opacity: 1, y: 0, transition: { duration: MAIN_ANIMATION.DURATION } }}
        exit={{ opacity: 0, y: -44, transition: { duration: MAIN_ANIMATION.DURATION } }}
      >
        <Container>
          {/*<MenuOption onClick={toggleDropdown(DROPDOWN_TYPES.SORT)}>*/}
          {/*  <FormattedMessage id="artworksView.sortMenu" defaultMessage="Sort" />*/}
          {/*</MenuOption>*/}

          <DesktopGroupingsContainer>
            {renderGroupings(!!groupings)}
            {renderAllArtworksItem(router.pathname === ROUTE.ARTWORKS)}
          </DesktopGroupingsContainer>

          {renderMobileGroupingsButton(router.pathname !== ROUTE.ARTWORKS)}
          {renderMobileAllArtworksItem(router.pathname === ROUTE.ARTWORKS)}

          <ControlsContainer isFilterSelected={!!filtersCount}>
            <GridSwitcher />

            {renderFilterButton(!router.pathname?.includes(ROUTE.GROUPINGS) && router.pathname !== ROUTE.TRADE)}
          </ControlsContainer>
        </Container>
        {renderFavoriteDropdown(isAuthed)}
        {renderFocusTrap(favoriteOpen)}
        <SortDropdown isOpen={dropdownOpen === DROPDOWN_TYPES.SORT} />
        <HeaderFilter options={filterOptions} open={filterOpen} onClose={() => setDropdownOpen(null)} />
      </MotionContainer>
    </Header>
  );

  const renderRegularMemberMessage = renderWhenTrue(
    always(<Title>Currently you&apos;re logged in as a regular member. Please log in as a trade member.</Title>)
  );

  const renderTradeMemberModal = renderWhenTrue(
    always(
      <TradeModal>
        <Content>
          <Title>This page is only available for Platform trade members.</Title>
          {renderRegularMemberMessage(userVariant === eUserVariants.REGULAR)}
          <Controls>
            <Button onClick={() => router.push(TRADE_PROGRAM_LANDING)}>Learn more</Button>
            <Button onClick={() => handleOnLoginClick()}>Log in</Button>
          </Controls>
        </Content>
      </TradeModal>
    )
  );

  const renderGroupingIntro = renderWhenTrue(
    always(
      <GroupingIntro>
        <GroupingTitle>{groupingTitle}</GroupingTitle>
        <GroupingDescription dangerouslySetInnerHTML={{ __html: groupingDescription }}></GroupingDescription>
      </GroupingIntro>
    )
  );

  return (
    <Layout title={headerTitle} defaultDescription={headerDescription} customHeader={renderCustomHeader}>
      {renderTradeMemberModal(!isPageAvailable && isTradeMemberRoute)}
      <ArtworksGrid
        groupingsIntro={renderGroupingIntro(!!groupings && router.pathname !== ROUTE.ARTWORKS)}
        artworks={artworks}
        gridSize={gridSize}
      />
    </Layout>
  );
};
